import React, { FunctionComponent, useCallback, useEffect, useState } from 'react'
import { Text, Skeleton, Link, useToast } from '@afterpaytouch/core'
import { useFlag } from '../../../hooks'
import { useTranslation } from 'next-i18next'
import { useGetOrderTransactionPpaQuery, useLazyGetOrderTransactionPpaDocumentQuery } from '../../../state/orders/hooks'
import { getPpaDocumentTitle } from '../../../state/orders/utils'

const I18N_NAMESPACE = ['common', 'orders']

interface OrderSnapshotLinksProps {
  orderId: string
}

export const OrderSnapshotLinks: FunctionComponent<OrderSnapshotLinksProps> = ({ orderId }) => {
  const { data, isFetching, isError, refetch } = useGetOrderTransactionPpaQuery(orderId)
  const isSecurePpaEnabled = useFlag('consumer-portal-ppa-link-display', false)
  const ppaDocsEndpointDrivenEnabled = useFlag('paylater.enable-ppa-docs-endpoint-driven', false)
  const { t } = useTranslation(I18N_NAMESPACE)
  const ppaDocsData = data?.ppaDocs
  const [documentId, setDocumentId] = useState<string>()
  const [getPpaDoc, { data: documentData, isError: documentError }] = useLazyGetOrderTransactionPpaDocumentQuery()
  const setToast = useToast()

  /**
   *
   * @deprecated to be removed when paylater.enable-ppa-docs-endpoint-driven flag is fulled rolled out
   * FF: 'paylater.enable-ppa-docs-endpoint-driven'
   */
  const documentTypeTranslation = {
    PAYMENT_AUTHORISATION: t('orders:orderSnapshot:ppa:paymentAuthorisation'),
    LOAN_AGREEMENT: t('orders:orderSnapshot:ppa:loanAgreement'),
    FINAL_LOAN_AGREEMENT: t('orders:ppa:finalLoanAgreement'),
  }

  const renderDocument = (docId: string): void => {
    setDocumentId(docId)
  }

  const refetchPpa = useCallback((): void => {
    if (isError) {
      refetch()
    }
  }, [isError, refetch])

  useEffect(() => {
    if (documentId !== undefined) {
      getPpaDoc(documentId)
    }
  }, [documentId, getPpaDoc])

  useEffect(() => {
    if (documentData?.documentId === documentId && documentId !== undefined) {
      try {
        localStorage.setItem('ppaHtml', JSON.stringify({ htmlToRender: documentData?.renderedHtml }))
      } catch (e) {}
      window.open(`ppa/${documentId}`, '_blank')
      setDocumentId(undefined)
    }
    if (documentError && Boolean(documentId)) {
      setToast({ message: t('orders:orderSnapshot:documentError'), kind: 'Failure' })
      setDocumentId(undefined)
    }
  }, [documentData, documentId, documentError, setToast, t])

  // @ts-ignore: OPERATION BLEED STOPPER
  return (isSecurePpaEnabled && ppaDocsData?.length > 0) || isFetching ? (
    <>
      <div className='relative sm:mb-2 md:mb-2 lg:mb-0'>
        <div className='relative md:mt-0'>
          {ppaDocsEndpointDrivenEnabled ? (
            <>
              {ppaDocsData?.map((doc) => (
                <div className='mt-0 md:mt-0' key={`ppaLink-${doc.documentId}`} data-testid='order-snapshot-link'>
                  <Link onClick={() => renderDocument(doc?.documentId)} kind={'Underline'} testNameSpace={`ppa-link-${doc?.documentId}`}>
                    <Text bold size={'XS'} testNameSpace={`ppa-link-${doc?.documentId}`}>
                      {t(`orders:ppa:${getPpaDocumentTitle(doc.docType)}`)}
                    </Text>
                  </Link>
                </div>
              ))}
            </>
          ) : (
            <>
              {ppaDocsData?.map((doc) =>
                documentTypeTranslation[doc.docType] !== undefined ? (
                  <div className='mt-0 md:mt-0' key={`ppaLink-${doc.documentId}`} data-testid='order-snapshot-link'>
                    <Link onClick={() => renderDocument(doc?.documentId)} kind={'Underline'} testNameSpace={`ppa-link-${doc?.documentId}`}>
                      <Text bold size={'XS'} testNameSpace={`ppa-link-${doc?.documentId}`}>
                        {documentTypeTranslation[doc.docType]}
                      </Text>
                    </Link>
                  </div>
                ) : null
              )}
            </>
          )}
        </div>
        {isFetching && (
          <>
            <div className='flex'>
              {Object.keys(documentTypeTranslation).map((key) => (
                <Text key={`ppaLink-loading-${key}`} bold size={'S'}>
                  {documentTypeTranslation[key]}
                </Text>
              ))}
            </div>
            <div className='absolute inset-0'>
              <Skeleton />
            </div>
          </>
        )}
      </div>
    </>
  ) : isError ? (
    <>
      <div className='relative'>
        <Text size={'M'} color='Fire'>
          {t('orders:orderSnapshot:ppaError')}
          <span className='cursor-pointer' onClick={refetchPpa}>
            <Text size={'M'} color='Fire' underline renderAs='span' bold>
              {t('orders:orderSnapshot:tryAgain')}
            </Text>
          </span>
        </Text>
      </div>
    </>
  ) : null
}
